import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService, AmazonClaimFilters } from "../services/api";

export const useAmazonClaims = (params?: AmazonClaimFilters) => {
  return useQuery({
    queryKey: ["amazonClaims", params],
    queryFn: () => apiService.getAmazonClaims(params),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useAmazonClaimStats = () => {
  return useQuery({
    queryKey: ["amazonClaimStats"],
    queryFn: () => apiService.getAmazonClaimStats(),
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useAmazonClaim = (claimId: string) => {
  return useQuery({
    queryKey: ["amazonClaim", claimId],
    queryFn: () => apiService.getAmazonClaim(claimId),
    enabled: !!claimId,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useReviewAmazonClaim = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      claimId,
      data,
    }: {
      claimId: string;
      data: {
        action: "approved" | "rejected";
        approvedAmount?: number;
        approvedCurrency?: string;
        commissionAmount?: number;
        rejectionReason?: string;
        adminNote?: string;
      };
    }) => apiService.reviewAmazonClaim(claimId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonClaims"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaim"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaimStats"] });
    },
  });
};

export const useUpdateOcrAmount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, amount }: { claimId: string; amount: number }) =>
      apiService.updateOcrAmount(claimId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonClaims"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaim"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaimStats"] });
    },
  });
};

export const useBulkReviewAmazonClaims = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      claimIds: string[];
      action: "approved" | "rejected";
      rejectionReason?: string;
      adminNote?: string;
    }) => apiService.bulkReviewAmazonClaims(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonClaims"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaimStats"] });
    },
  });
};

export const useMarkAmazonClaimPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      claimId,
      payoutId,
    }: {
      claimId: string;
      payoutId: string;
    }) => apiService.markAmazonClaimPaid(claimId, payoutId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonClaims"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaim"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaimStats"] });
    },
  });
};

export const useAmazonConfig = () => {
  return useQuery({
    queryKey: ["amazonConfig"],
    queryFn: () => apiService.getAmazonConfig(),
    refetchOnWindowFocus: false,
    staleTime: 30 * 60 * 1000,
  });
};

export const useUpdateAmazonConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cashbackPercent: number) =>
      apiService.updateAmazonConfig(cashbackPercent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonConfig"] });
    },
  });
};

export const useBulkImportSingleAidaForm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      email: string;
      aidaFormId: string;
      screenshots: string[];
      cashbackRate?: number;
    }) => apiService.bulkImportSingleAidaForm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonClaims"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaimStats"] });
    },
  });
};

export const useCheckCustomersBatch = () => {
  return useMutation({
    mutationFn: (emails: string[]) => apiService.checkCustomersBatch(emails),
  });
};

export const useTriggerAutoApprove = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiService.triggerAutoApprove(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["amazonClaims"] });
      queryClient.invalidateQueries({ queryKey: ["amazonClaimStats"] });
    },
  });
};
